import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Reveal, MaskedLine } from "./Reveal";

const PHOTOS = [
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/c8026f2736247654_Dynamic_pan_around_202602242107_k0wbs.00_03_32_07.Still009.jpg",
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/1899917c667663ac_Dynamic_pan_around_202602242107_k0wbs.00_02_45_14.Still004.jpg",
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/2a8994ca0cc2a6d7_Dynamic_pan_around_202602242107_k0wbs.00_01_08_11.Still003.jpg",
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/8f571620bfd957b0_Full_stage_remove_2k_202602222014.webp",
];

const PLATE_MIC =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/507dc4cef9270343_Dec_19__1631_17s_202512191700_8cw0o.00_00_50_08.Still002.jpg";
const PLATE_REHEARSAL =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/595baebfca511533_Different_pose_sassy_2k_202602051746.00_00_45_23.Still010.webp";
const PLATE_BROADCAST =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/a929ee4eee9a8738_Image_202601232034.00_00_13_13.Still009.jpg";
const PLATE_STAGE =
  "https://customer-assets-v7afamib.emergentagent.net/job_danimorr/artifacts/18156bd9b34b358a_Pose_different_sensual_2k_202602231749%20%281%29.webp";

const Plate = ({ testid, src, alt, caption, tilt, className = "", border = "border-gold/30" }) => (
  <figure data-testid={testid} className={`${tilt} ${className}`}>
    <div className={`clip-frame border ${border} overflow-hidden shadow-2xl`}>
      <img src={src} alt={alt} className="w-full h-40 lg:h-48 object-cover analog-grade" loading="lazy" />
    </div>
    <figcaption className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream/35 mt-2">
      {caption}
    </figcaption>
  </figure>
);

const DanceInterlude = () => {
  const [visible, setVisible] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setFlipped((f) => !f), 4200);
    return () => clearInterval(t);
  }, []);

  const next = (visible + 1) % PHOTOS.length;
  const frontSrc = flipped ? PHOTOS[next] : PHOTOS[visible];
  const backSrc = flipped ? PHOTOS[visible] : PHOTOS[next];

  return (
    <section
      data-testid="dance-section"
      className="relative bg-ink border-t border-cream/10 py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="font-mono text-xs text-gold tracking-widest mb-6">— INTERLUDE II</p>
            </Reveal>
            <h2 className="font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <MaskedLine delay={0.05}>For the love of</MaskedLine>
              <MaskedLine delay={0.15}>
                <em className="italic text-gold">dance</em> &amp; <em className="italic text-amber">TVs.</em>
              </MaskedLine>
            </h2>
            <Reveal delay={0.25}>
              <p className="text-base lg:text-lg text-cream/70 leading-relaxed mt-7 max-w-xl">
                My work exists at the intersection of sound, movement, and visual worldbuilding.
                Grounded in a deep love for rhythm, poetic lyricism, and tactile nostalgia, I
                craft immersive experiences that bridge the digital and the analog.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="flip-perspective max-w-xl mx-auto" data-testid="photo-flip">
                <motion.div
                  className="flip-inner aspect-video"
                  animate={{ rotateY: flipped ? 180 : 0 }}
                  transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1] }}
                  onAnimationComplete={() => setVisible(next)}
                >
                  <div className="flip-face scanlines clip-frame border border-gold/40">
                    <img
                      src={frontSrc}
                      alt="Dani Morr — dance and television frame"
                      className="w-full h-full object-cover"
                      data-testid="flip-front"
                    />
                  </div>
                  <div className="flip-face flip-back scanlines clip-frame-tl border border-gold/40">
                    <img
                      src={backSrc}
                      alt="Dani Morr — dance and television frame"
                      className="w-full h-full object-cover"
                      data-testid="flip-back"
                    />
                  </div>
                </motion.div>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-cream/50">
                    CH 04 — DANCE / ON LOOP
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.25em] text-gold flex items-center gap-2">
                    <span className="vu-light" style={{ backgroundColor: "#A3562A", animationDuration: "1.1s" }} />
                    REC
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 mt-16 lg:mt-20">
          <div className="hidden lg:flex lg:col-span-2 flex-col justify-between py-6" data-testid="rail-left">
            <Plate
              testid="plate-mic"
              src={PLATE_MIC}
              alt="Silhouette at the microphone, halo of gold light"
              caption="Mic / Halo"
              tilt="-rotate-3"
            />
            <Plate
              testid="plate-broadcast"
              src={PLATE_BROADCAST}
              alt="Dancer bending beside a stack of vintage televisions"
              caption="Broadcast / Body"
              tilt="rotate-2"
              className="translate-x-4"
            />
          </div>

          <div className="lg:col-span-6 lg:col-start-4">
            <Reveal delay={0.1}>
              <div className="max-w-xl mx-auto space-y-7 border-t border-cream/10 pt-8">
                <p className="text-sm text-cream/55 leading-relaxed">
                  I am an artist driven by the need to build entire worlds out of sound, motion,
                  and visual texture. My work spans music production, dance, filmmaking, and
                  visual design—each medium serving as a different frequency to express human
                  emotion, storytelling, and rhythm. There is an undeniable magic in blending
                  raw, soulful rhythm with a distinct visual aesthetic. Whether I am composing
                  atmospheric tracks, choreographing movement, or directing visuals, my creative
                  process is rooted in depth, texture, and authenticity.
                </p>
                <div>
                  <h3 className="font-serif italic text-xl text-gold mb-2">Sound &amp; Movement</h3>
                  <p className="text-sm text-cream/55 leading-relaxed">
                    Music and dance are the heartbeats of my creative identity. Crafting music
                    allows me to construct emotional architecture—layering rich harmonies,
                    intricate beats, and poetic lyricism to shape time and tone. Movement is the
                    physical extension of that sound. Dancing brings raw presence and immediate,
                    unfiltered expression, translating feelings that words alone cannot capture.
                    The interplay between sonic rhythm and physical body drives everything I create.
                  </p>
                </div>
                <div>
                  <h3 className="font-serif italic text-xl text-gold mb-2">
                    Visual Worldbuilding &amp; The Screen Aesthetic
                  </h3>
                  <p className="text-sm text-cream/55 leading-relaxed">
                    Visually, I am drawn to texture, nostalgia, and immersive framing. I frequently
                    frame my visual work inside vintage television screens, CRT displays, and
                    analog textures. In a modern digital landscape that often feels clinical and
                    hyper-polished, the warmth of scan lines, screen glow, and broadcast-style
                    frames introduces a sense of soul, tactile depth, and intimacy. A TV screen
                    acts as a portal. By placing video inside a monitor, the content transforms
                    from a simple recording into a curated broadcast from an alternate universe.
                    It establishes a intentional boundary between reality and the world I've
                    built, inviting the viewer to step back, observe, and immerse themselves in a
                    specific moment in time.
                  </p>
                </div>
                <div className="relative">
                  <motion.div
                    data-testid="vision-glow"
                    aria-hidden="true"
                    className="absolute -inset-x-8 -inset-y-4 pointer-events-none rounded-full"
                    style={{
                      background:
                        "radial-gradient(ellipse 75% 65% at 50% 100%, rgba(193,140,61,0.18), transparent 70%)",
                    }}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1.6, ease: "easeOut" }}
                  />
                  <h3 className="relative font-serif italic text-xl text-gold mb-2">The Vision</h3>
                  <p className="relative text-sm text-cream/55 leading-relaxed">
                    At its core, my art is about agency, storytelling, and capturing the
                    intangible. Life moves fast, but through music, film, and visual design, I am
                    able to freeze moods, document complex narratives, and craft experiences that
                    linger. Welcome to my world—where rhythm meets vision, and every frame tells
                    a story.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="hidden lg:flex lg:col-span-2 flex-col justify-between py-6" data-testid="rail-right">
            <Plate
              testid="plate-rehearsal"
              src={PLATE_REHEARSAL}
              alt="Dani dancing in a brick rehearsal studio"
              caption="Rehearsal / Brick &amp; Sweat"
              tilt="rotate-3"
              className="-translate-x-2 mt-10"
              border="border-amber/40"
            />
            <Plate
              testid="plate-stage"
              src={PLATE_STAGE}
              alt="Dani in a backbend arc on a dark stage"
              caption="Stage / Arc"
              tilt="-rotate-2"
              className="translate-x-3"
              border="border-amber/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-5 lg:hidden" data-testid="rail-mobile">
            <Plate testid="plate-mic-m" src={PLATE_MIC} alt="Silhouette at the microphone" caption="Mic / Halo" tilt="-rotate-2" />
            <Plate testid="plate-rehearsal-m" src={PLATE_REHEARSAL} alt="Dani dancing in a brick studio" caption="Rehearsal" tilt="rotate-2" />
            <Plate testid="plate-broadcast-m" src={PLATE_BROADCAST} alt="Dancer beside stacked televisions" caption="Broadcast / Body" tilt="rotate-1" />
            <Plate testid="plate-stage-m" src={PLATE_STAGE} alt="Dani in a backbend on stage" caption="Stage / Arc" tilt="-rotate-1" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DanceInterlude;
